// Empire Apps Chassis · elder · i18n strings
// Pre-translated voice + UI strings for 5 Indian languages + English.
// Source: human-translated reference set + Sarvabhasha voice-v2 consistency.
// Keys are stable; values can be revised without breaking call sites.

import type { I18nDictionary, LangCode } from "./types.ts";

export const i18n: I18nDictionary = {
  // Core navigation
  "nav.back": {
    hi: "वापस जाएँ",
    ta: "திரும்பிச் செல்லுங்கள்",
    bn: "ফিরে যান",
    mr: "मागे जा",
    gu: "પાછા જાઓ",
    en: "Go back",
  },
  "nav.help": {
    hi: "मदद चाहिए",
    ta: "உதவி தேவை",
    bn: "সাহায্য চাই",
    mr: "मदत हवी",
    gu: "મદદ જોઈએ",
    en: "I need help",
  },
  "nav.repeat": {
    hi: "फिर से बोलिए",
    ta: "மீண்டும் சொல்லுங்கள்",
    bn: "আবার বলুন",
    mr: "पुन्हा सांगा",
    gu: "ફરી કહો",
    en: "Say again",
  },
  // Tutor scaffolds
  "tutor.welcome": {
    hi: "नमस्ते, मैं आपका सारथी हूँ। चलिए मिलकर सीखते हैं।",
    ta: "வணக்கம், நான் உங்கள் சாரதி. ஒன்றாகக் கற்றுக்கொள்ளலாம்.",
    bn: "নমস্কার, আমি আপনার সারথি। চলুন একসাথে শিখি।",
    mr: "नमस्कार, मी तुमचा सारथी आहे. आपण एकत्र शिकूया.",
    gu: "નમસ્તે, હું તમારો સારથિ છું. ચાલો સાથે શીખીએ.",
    en: "Hello, I am your Sarathi. Let us learn together.",
  },
  "tutor.tap_to_continue": {
    hi: "जब तैयार हों तो हरे बटन पर दबाइए",
    ta: "தயாராக இருக்கும்போது பச்சை பொத்தானை தட்டுங்கள்",
    bn: "প্রস্তুত হলে সবুজ বোতামে চাপুন",
    mr: "तयार झाल्यावर हिरव्या बटणावर दाबा",
    gu: "તૈયાર હો ત્યારે લીલા બટન પર દબાવો",
    en: "Press the green button when ready",
  },
  "tutor.well_done": {
    hi: "बहुत बढ़िया! आपने कर दिखाया।",
    ta: "மிக நல்லது! நீங்கள் செய்துவிட்டீர்கள்.",
    bn: "খুব ভালো! আপনি করে দেখিয়েছেন।",
    mr: "अप्रतिम! तुम्ही करून दाखवलं.",
    gu: "ખૂબ સરસ! તમે કરી બતાવ્યું.",
    en: "Excellent. You did it.",
  },
  "tutor.try_again": {
    hi: "कोई बात नहीं, फिर से कोशिश करते हैं।",
    ta: "பிரச்சினை இல்லை, மீண்டும் முயற்சி செய்வோம்.",
    bn: "কিছু হয়নি, আবার চেষ্টা করি।",
    mr: "काही हरकत नाही, परत प्रयत्न करूया.",
    gu: "કાંઈ વાંધો નથી, ફરી પ્રયત્ન કરીએ.",
    en: "It is okay. Let us try again.",
  },
  "tutor.aborted": {
    hi: "हम बाद में सीखेंगे। आराम कीजिए।",
    ta: "பின்னர் கற்றுக்கொள்வோம். ஓய்வு எடுங்கள்.",
    bn: "পরে শিখব। বিশ্রাম করুন।",
    mr: "नंतर शिकू. विश्रांती घ्या.",
    gu: "પછી શીખશું. આરામ કરો.",
    en: "Let us learn later. Rest a moment.",
  },
  // WhatsApp send message task
  "task.whatsapp.send_message.label": {
    hi: "व्हाट्सऐप पर संदेश भेजना",
    ta: "வாட்ஸ்அப்பில் செய்தி அனுப்புவது",
    bn: "হোয়াটসঅ্যাপে বার্তা পাঠানো",
    mr: "व्हॉट्सअ‍ॅपवर संदेश पाठवणे",
    gu: "વોટ્સએપ પર સંદેશ મોકલવો",
    en: "Send a WhatsApp message",
  },
  "task.whatsapp.send_message.step1": {
    hi: "पहले व्हाट्सऐप का हरा आइकन ढूँढिए और दबाइए।",
    ta: "முதலில் வாட்ஸ்அப்பின் பச்சை சின்னத்தை கண்டுபிடித்து தட்டுங்கள்.",
    bn: "প্রথমে হোয়াটসঅ্যাপের সবুজ আইকনটি খুঁজুন এবং চাপুন।",
    mr: "आधी व्हॉट्सअ‍ॅपचा हिरवा चिन्ह शोधा आणि दाबा.",
    gu: "પ્રથમ વોટ્સએપનું લીલું ચિહ્ન શોધો અને દબાવો.",
    en: "First, find the green WhatsApp icon and tap it.",
  },
  "task.whatsapp.send_message.step2": {
    hi: "अब उस व्यक्ति का नाम चुनिए जिसे संदेश भेजना है।",
    ta: "செய்தி அனுப்ப விரும்பும் நபரின் பெயரை இப்போது தேர்ந்தெடுங்கள்.",
    bn: "এখন যাকে বার্তা পাঠাবেন সেই ব্যক্তির নাম বেছে নিন।",
    mr: "आता ज्यांना संदेश पाठवायचा त्यांचं नाव निवडा.",
    gu: "હવે જે વ્યક્તિને સંદેશ મોકલવો છે તેમનું નામ પસંદ કરો.",
    en: "Now pick the name of the person you want to message.",
  },
  "task.whatsapp.send_message.step3": {
    hi: "नीचे लिखने वाले डिब्बे में अपना संदेश टाइप कीजिए, या माइक का बटन दबाकर बोलिए।",
    ta: "கீழே உள்ள உள்ளீட்டுப் பெட்டியில் உங்கள் செய்தியை தட்டச்சு செய்யுங்கள், அல்லது மைக் பொத்தானை அழுத்தி பேசுங்கள்.",
    bn: "নিচের লেখার বক্সে আপনার বার্তা লিখুন, অথবা মাইক বোতাম চেপে বলুন।",
    mr: "खालच्या टाईप करण्याच्या बॉक्समध्ये तुमचा संदेश लिहा, किंवा माइक बटण दाबून बोला.",
    gu: "નીચે ટાઇપ બોક્સમાં તમારો સંદેશ ટાઇપ કરો, અથવા માઇક બટન દબાવી બોલો.",
    en: "Type your message in the text box below, or press the mic button and speak.",
  },
  "task.whatsapp.send_message.step4": {
    hi: "तीर वाला बटन दबाइए — संदेश चला जाएगा।",
    ta: "அம்பு பொத்தானை அழுத்துங்கள் — செய்தி அனுப்பப்படும்.",
    bn: "তীর বোতাম চাপুন — বার্তা চলে যাবে।",
    mr: "बाण असलेले बटण दाबा — संदेश पाठवला जाईल.",
    gu: "તીરનું બટન દબાવો — સંદેશ ચાલ્યો જશે.",
    en: "Press the arrow button. The message will be sent.",
  },
  // WhatsApp video call
  "task.whatsapp.video_call.label": {
    hi: "व्हाट्सऐप पर वीडियो कॉल लगाना",
    ta: "வாட்ஸ்அப்பில் வீடியோ அழைப்பு செய்வது",
    bn: "হোয়াটসঅ্যাপে ভিডিও কল করা",
    mr: "व्हॉट्सअ‍ॅपवर व्हिडिओ कॉल लावणे",
    gu: "વોટ્સએપ પર વિડિયો કોલ કરવો",
    en: "Make a WhatsApp video call",
  },
  "task.whatsapp.video_call.step1": {
    hi: "व्हाट्सऐप खोलिए और जिस व्यक्ति को कॉल करना है उसका नाम चुनिए।",
    ta: "வாட்ஸ்அப்பைத் திறந்து, அழைக்க விரும்பும் நபரின் பெயரை தேர்ந்தெடுங்கள்.",
    bn: "হোয়াটসঅ্যাপ খুলুন এবং যাকে কল করবেন তার নাম বেছে নিন।",
    mr: "व्हॉट्सअ‍ॅप उघडा आणि ज्यांना कॉल करायचं त्यांचं नाव निवडा.",
    gu: "વોટ્સએપ ખોલો અને જે વ્યક્તિને કોલ કરવો છે તેમનું નામ પસંદ કરો.",
    en: "Open WhatsApp and pick the name of the person you want to call.",
  },
  "task.whatsapp.video_call.step2": {
    hi: "ऊपर दाएँ कोने में कैमरे का चिन्ह दबाइए।",
    ta: "மேல் வலதுபுற மூலையில் கேமரா சின்னத்தைத் தட்டுங்கள்.",
    bn: "উপরের ডান কোণে ক্যামেরা চিহ্ন চাপুন।",
    mr: "वरच्या उजव्या कोपऱ्यात कॅमेरा चिन्हावर दाबा.",
    gu: "ઉપર જમણા ખૂણામાં કેમેરા ચિહ્ન દબાવો.",
    en: "Tap the camera icon in the top-right corner.",
  },
  "task.whatsapp.video_call.step3": {
    hi: "अब प्रतीक्षा कीजिए। दूसरी तरफ से उठाते ही चेहरा दिखेगा।",
    ta: "இப்போது காத்திருங்கள். மறுபக்கம் எடுத்தவுடன் முகம் தெரியும்.",
    bn: "এখন অপেক্ষা করুন। ওপাশ থেকে ধরলে মুখ দেখাবে।",
    mr: "आता वाट पाहा. दुसऱ्या बाजूने उचलल्यावर चेहरा दिसेल.",
    gu: "હવે રાહ જુઓ. સામેથી ઉઠાવ્યા પછી ચહેરો દેખાશે.",
    en: "Now wait. When they answer, you will see their face.",
  },
  // Phone dial
  "task.phone.dial.label": {
    hi: "फोन से कॉल लगाना",
    ta: "தொலைபேசியில் அழைப்பு செய்வது",
    bn: "ফোন থেকে কল করা",
    mr: "फोनवरून कॉल लावणे",
    gu: "ફોન પરથી કોલ કરવો",
    en: "Make a phone call",
  },
  "task.phone.dial.step1": {
    hi: "हरे फोन के चिन्ह वाला ऐप खोलिए।",
    ta: "பச்சை தொலைபேசி சின்னத்துடன் கூடிய பயன்பாட்டைத் திறக்கவும்.",
    bn: "সবুজ ফোন চিহ্নের অ্যাপটি খুলুন।",
    mr: "हिरव्या फोनच्या चिन्हाचा अ‍ॅप उघडा.",
    gu: "લીલા ફોન ચિહ્નવાળી એપ્લિકેશન ખોલો.",
    en: "Open the app with the green phone icon.",
  },
  "task.phone.dial.step2": {
    hi: "नंबर डायल पैड पर टाइप कीजिए, या संपर्क में से नाम चुनिए।",
    ta: "எண்களை டயல் பேடில் தட்டச்சு செய்யுங்கள், அல்லது தொடர்புகளில் இருந்து பெயரை தேர்ந்தெடுங்கள்.",
    bn: "ডায়াল প্যাডে নম্বর টাইপ করুন, অথবা পরিচিতি থেকে নাম বেছে নিন।",
    mr: "डायल पॅडवर नंबर टाईप करा, किंवा संपर्कांमधून नाव निवडा.",
    gu: "ડાયલ પેડ પર નંબર ટાઇપ કરો, અથવા સંપર્કમાંથી નામ પસંદ કરો.",
    en: "Type the number on the dial pad, or pick from contacts.",
  },
  "task.phone.dial.step3": {
    hi: "हरे कॉल बटन पर दबाइए।",
    ta: "பச்சை அழைப்பு பொத்தானை அழுத்துங்கள்.",
    bn: "সবুজ কল বোতামে চাপুন।",
    mr: "हिरव्या कॉल बटणावर दाबा.",
    gu: "લીલા કોલ બટન પર દબાવો.",
    en: "Press the green call button.",
  },
  // Camera take photo
  "task.camera.take_photo.label": {
    hi: "फोटो खींचना",
    ta: "புகைப்படம் எடுப்பது",
    bn: "ফটো তোলা",
    mr: "फोटो काढणे",
    gu: "ફોટો લેવો",
    en: "Take a photo",
  },
  "task.camera.take_photo.step1": {
    hi: "कैमरा ऐप खोलिए। फोन के पीछे का कैमरा डिफ़ॉल्ट होता है।",
    ta: "கேமரா பயன்பாட்டைத் திறக்கவும். தொலைபேசியின் பின்புற கேமரா இயல்பாகும்.",
    bn: "ক্যামেরা অ্যাপ খুলুন। ফোনের পিছনের ক্যামেরা ডিফল্ট।",
    mr: "कॅमेरा अ‍ॅप उघडा. फोनच्या मागचा कॅमेरा डिफॉल्ट असतो.",
    gu: "કેમેરા એપ ખોલો. ફોનની પાછળનો કેમેરા ડિફોલ્ટ હોય છે.",
    en: "Open the camera app. The rear camera is the default.",
  },
  "task.camera.take_photo.step2": {
    hi: "जो दृश्य कैद करना है उसे फोन की स्क्रीन पर देखिए।",
    ta: "எடுக்க விரும்பும் காட்சியை தொலைபேசியின் திரையில் பார்க்கவும்.",
    bn: "যে দৃশ্যটি তুলতে চান সেটি ফোনের স্ক্রিনে দেখুন।",
    mr: "जे दृश्य पकडायचं ते फोनच्या स्क्रीनवर पाहा.",
    gu: "જે દૃશ્ય કેદ કરવું છે તે ફોનની સ્ક્રીન પર જુઓ.",
    en: "Frame the scene you want to capture on the screen.",
  },
  "task.camera.take_photo.step3": {
    hi: "बीच के बड़े गोल बटन पर दबाइए। फोटो खिंच जाएगी।",
    ta: "நடுவில் உள்ள பெரிய வட்ட பொத்தானை அழுத்துங்கள். புகைப்படம் எடுக்கப்படும்.",
    bn: "মাঝখানের বড় গোলাকার বোতাম চাপুন। ফটো উঠে যাবে।",
    mr: "मधल्या मोठ्या गोल बटणावर दाबा. फोटो निघेल.",
    gu: "વચ્ચેના મોટા ગોળ બટન પર દબાવો. ફોટો પડી જશે.",
    en: "Press the large round button in the middle. The photo will be taken.",
  },
  // Gallery share photo
  "task.gallery.share_photo.label": {
    hi: "फोटो साझा करना",
    ta: "புகைப்படத்தை பகிர்வது",
    bn: "ফটো শেয়ার করা",
    mr: "फोटो शेअर करणे",
    gu: "ફોટો શેર કરવો",
    en: "Share a photo",
  },
  "task.gallery.share_photo.step1": {
    hi: "गैलरी या फोटो ऐप खोलिए।",
    ta: "காட்சி அல்லது புகைப்படம் பயன்பாட்டைத் திறக்கவும்.",
    bn: "গ্যালারি বা ফটো অ্যাপ খুলুন।",
    mr: "गॅलरी किंवा फोटो अ‍ॅप उघडा.",
    gu: "ગેલેરી અથવા ફોટો એપ ખોલો.",
    en: "Open the gallery or photos app.",
  },
  "task.gallery.share_photo.step2": {
    hi: "जो फोटो भेजना है उस पर दबाइए। फिर 'शेयर' का बटन ढूँढिए।",
    ta: "அனுப்ப விரும்பும் புகைப்படத்தை அழுத்துங்கள். பிறகு 'பகிர்' பொத்தானை கண்டுபிடியுங்கள்.",
    bn: "যে ফটো পাঠাতে চান সেটিতে চাপুন। তারপর 'শেয়ার' বোতাম খুঁজুন।",
    mr: "जो फोटो पाठवायचा त्यावर दाबा. नंतर 'शेअर' बटण शोधा.",
    gu: "જે ફોટો મોકલવો છે તે પર દબાવો. પછી 'શેર' બટન શોધો.",
    en: "Tap the photo you want to send, then find the 'Share' button.",
  },
  "task.gallery.share_photo.step3": {
    hi: "व्हाट्सऐप का चिन्ह चुनिए, फिर व्यक्ति का नाम चुनिए, फिर भेजिए।",
    ta: "வாட்ஸ்அப் சின்னத்தைத் தேர்ந்தெடுங்கள், பிறகு நபரின் பெயரை தேர்ந்தெடுத்து அனுப்புங்கள்.",
    bn: "হোয়াটসঅ্যাপ চিহ্ন বেছে নিন, তারপর ব্যক্তির নাম বেছে পাঠান।",
    mr: "व्हॉट्सअ‍ॅप चिन्ह निवडा, नंतर व्यक्तीचं नाव निवडून पाठवा.",
    gu: "વોટ્સએપ ચિહ્ન પસંદ કરો, પછી વ્યક્તિનું નામ પસંદ કરો, અને મોકલો.",
    en: "Pick the WhatsApp icon, choose the person, then send.",
  },
  // UPI send money to contact
  "task.upi.send_money_to_contact.label": {
    hi: "किसी अपने को पैसे भेजना",
    ta: "உங்கள் நபருக்கு பணம் அனுப்புவது",
    bn: "নিজের কাউকে টাকা পাঠানো",
    mr: "आपल्या व्यक्तीला पैसे पाठवणे",
    gu: "પોતાના કોઈને પૈસા મોકલવા",
    en: "Send money to someone you know",
  },
  "task.upi.send_money_to_contact.step1": {
    hi: "जरूरी सूचना: कभी भी अनजान व्यक्ति को OTP या UPI पिन मत बताइए। बैंक कभी नहीं माँगता।",
    ta: "முக்கிய அறிவிப்பு: அறிமுகமில்லாதவருக்கு OTP அல்லது UPI பின் ஒருபோதும் சொல்லாதீர்கள். வங்கி கேட்காது.",
    bn: "গুরুত্বপূর্ণ সতর্কতা: অপরিচিত কাউকে কখনও OTP বা UPI পিন বলবেন না। ব্যাংক কখনও জিজ্ঞেস করে না।",
    mr: "महत्त्वाची सूचना: कधीही अनोळखी व्यक्तीला OTP किंवा UPI पिन सांगू नका. बँक कधीही विचारत नाही.",
    gu: "મહત્વપૂર્ણ સૂચના: ક્યારેય અજાણ વ્યક્તિને OTP કે UPI પિન ન કહો. બેંક ક્યારેય પૂછતી નથી.",
    en: "Important: never share your OTP or UPI PIN with strangers. Your bank never asks.",
  },
  "task.upi.send_money_to_contact.step2": {
    hi: "अपनी पसंदीदा पैसे भेजने वाली ऐप खोलिए — फोनपे, गूगल पे, या पेटीएम।",
    ta: "உங்களுக்கு பிடித்த பணம் அனுப்பும் பயன்பாட்டை திறக்கவும் — பேடிஎம், கூகுள் பே, அல்லது போனிபே.",
    bn: "আপনার পছন্দের টাকা পাঠানোর অ্যাপ খুলুন — ফোনপে, গুগল পে, বা পেটিএম।",
    mr: "तुमचा आवडीचा पैसे पाठवण्याचा अ‍ॅप उघडा — फोनपे, गूगल पे, किंवा पेटीएम.",
    gu: "તમારી પસંદગીની પૈસા મોકલવાની એપ ખોલો — ફોનપે, ગૂગલ પે, અથવા પેટીએમ.",
    en: "Open your preferred payment app — PhonePe, Google Pay, or Paytm.",
  },
  "task.upi.send_money_to_contact.step3": {
    hi: "'भेजें' या 'Send' पर दबाइए। फिर अपने व्यक्ति का नाम चुनिए।",
    ta: "'அனுப்பு' அல்லது 'Send' அழுத்துங்கள். பிறகு உங்கள் நபரின் பெயரை தேர்ந்தெடுங்கள்.",
    bn: "'পাঠান' বা 'Send' চাপুন। তারপর আপনার ব্যক্তির নাম বেছে নিন।",
    mr: "'पाठवा' किंवा 'Send' वर दाबा. नंतर तुमच्या व्यक्तीचं नाव निवडा.",
    gu: "'મોકલો' અથવા 'Send' દબાવો. પછી તમારી વ્યક્તિનું નામ પસંદ કરો.",
    en: "Press 'Send'. Then choose the name of your person.",
  },
  "task.upi.send_money_to_contact.step4": {
    hi: "रकम लिखिए और 'आगे' पर दबाइए। फिर अपना UPI पिन डालिए। पैसा चला जाएगा।",
    ta: "தொகையை எழுதி 'அடுத்தது' அழுத்துங்கள். பிறகு உங்கள் UPI பின்னை உள்ளிடவும். பணம் அனுப்பப்படும்.",
    bn: "পরিমাণ লিখুন এবং 'এগিয়ে' চাপুন। তারপর আপনার UPI পিন দিন। টাকা চলে যাবে।",
    mr: "रक्कम लिहा आणि 'पुढे' दाबा. नंतर तुमचा UPI पिन टाका. पैसे निघून जातील.",
    gu: "રકમ લખો અને 'આગળ' દબાવો. પછી તમારો UPI પિન દાખલ કરો. પૈસા જતા રહેશે.",
    en: "Type the amount and press 'Next'. Then enter your UPI PIN. The money will be sent.",
  },
  // Voice note
  "task.whatsapp.send_voice_note.label": {
    hi: "व्हाट्सऐप पर आवाज़ का संदेश भेजना",
    ta: "வாட்ஸ்அப்பில் குரல் செய்தி அனுப்புவது",
    bn: "হোয়াটসঅ্যাপে ভয়েস বার্তা পাঠানো",
    mr: "व्हॉट्सअ‍ॅपवर आवाजाचा संदेश पाठवणे",
    gu: "વોટ્સએપ પર વોઇસ સંદેશ મોકલવો",
    en: "Send a WhatsApp voice note",
  },
  "task.whatsapp.send_voice_note.step1": {
    hi: "व्हाट्सऐप खोलकर अपने व्यक्ति की चैट चुनिए।",
    ta: "வாட்ஸ்அப்பை திறந்து உங்கள் நபரின் அரட்டையை தேர்ந்தெடுங்கள்.",
    bn: "হোয়াটসঅ্যাপ খুলে আপনার ব্যক্তির চ্যাট বেছে নিন।",
    mr: "व्हॉट्सअ‍ॅप उघडून तुमच्या व्यक्तीची चॅट निवडा.",
    gu: "વોટ્સએપ ખોલીને તમારી વ્યક્તિની ચેટ પસંદ કરો.",
    en: "Open WhatsApp and pick your person's chat.",
  },
  "task.whatsapp.send_voice_note.step2": {
    hi: "नीचे दाएँ कोने में माइक का चिन्ह दबाकर पकड़े रहिए और बोलिए।",
    ta: "கீழ் வலதுபுற மூலையில் மைக் சின்னத்தை அழுத்திப் பிடித்து பேசுங்கள்.",
    bn: "নিচের ডান কোণে মাইক চিহ্ন চেপে ধরে কথা বলুন।",
    mr: "खालच्या उजव्या कोपऱ्यात माइकचा चिन्ह दाबून धरून बोला.",
    gu: "નીચે જમણા ખૂણામાં માઇક ચિહ્ન દબાવી પકડી રાખો અને બોલો.",
    en: "In the bottom-right corner, press and hold the mic icon, then speak.",
  },
  "task.whatsapp.send_voice_note.step3": {
    hi: "बोलने के बाद उँगली उठाइए। संदेश अपने आप चला जाएगा।",
    ta: "பேசிய பிறகு விரலை எடுங்கள். செய்தி தானாக அனுப்பப்படும்.",
    bn: "বলা শেষ হলে আঙুল তুলে দিন। বার্তা নিজে চলে যাবে।",
    mr: "बोलून झाल्यावर बोट उचला. संदेश आपोआप पाठवला जाईल.",
    gu: "બોલ્યા પછી આંગળી ઉઠાવો. સંદેશ આપોઆપ જતો રહેશે.",
    en: "Lift your finger when you are done. The message will send by itself.",
  },
  // YouTube search song
  "task.youtube.search_song.label": {
    hi: "यू-ट्यूब पर भजन या गाना ढूँढना",
    ta: "யூட்யூப்பில் பாடல் தேடுவது",
    bn: "ইউটিউবে গান খোঁজা",
    mr: "यूट्यूबवर भजन किंवा गाणं शोधणं",
    gu: "યૂટ્યૂબ પર ભજન કે ગીત શોધવું",
    en: "Search for a song or bhajan on YouTube",
  },
  "task.youtube.search_song.step1": {
    hi: "लाल बटन वाला यू-ट्यूब ऐप खोलिए।",
    ta: "சிவப்பு பொத்தான் கொண்ட யூட்யூப் பயன்பாட்டை திறக்கவும்.",
    bn: "লাল বোতামের ইউটিউব অ্যাপ খুলুন।",
    mr: "लाल बटण असलेला यूट्यूब अ‍ॅप उघडा.",
    gu: "લાલ બટનવાળી યૂટ્યૂબ એપ ખોલો.",
    en: "Open the YouTube app with the red button.",
  },
  "task.youtube.search_song.step2": {
    hi: "ऊपर बीच में लूप वाले चिन्ह पर दबाइए — वही ढूँढने का बटन है।",
    ta: "மேல் நடுவில் உள்ள பெருக்கி கண்ணாடி சின்னத்தை அழுத்துங்கள் — அதுவே தேடல் பொத்தான்.",
    bn: "উপরে মাঝখানে ম্যাগনিফায়ার চিহ্নে চাপুন — সেটিই খোঁজার বোতাম।",
    mr: "वर मध्यभागी भिंगाच्या चिन्हावर दाबा — तेच शोध बटण आहे.",
    gu: "ઉપર વચ્ચે મેગ્નિફાયર ચિહ્ન પર દબાવો — તે જ શોધ બટન છે.",
    en: "Press the magnifier icon at the top centre. That is the search button.",
  },
  "task.youtube.search_song.step3": {
    hi: "गाने या भजन का नाम लिखिए या माइक का बटन दबाकर बोलिए।",
    ta: "பாடல் அல்லது பஜனையின் பெயரை எழுதுங்கள் அல்லது மைக் பொத்தானை அழுத்தி பேசுங்கள்.",
    bn: "গান বা ভজনের নাম লিখুন বা মাইক বোতাম চেপে বলুন।",
    mr: "गाण्याचं किंवा भजनाचं नाव लिहा किंवा माइक बटण दाबून बोला.",
    gu: "ગીત કે ભજનનું નામ લખો અથવા માઇક બટન દબાવી બોલો.",
    en: "Type the song or bhajan name, or press the mic and speak.",
  },
  "task.youtube.search_song.step4": {
    hi: "जो गाना दिखे, उस पर दबाइए। आवाज़ बढ़ाने के लिए दाहिनी ओर की पट्टी छुएँ।",
    ta: "காட்டப்படும் பாடலை அழுத்துங்கள். ஒலியை அதிகரிக்க வலதுபுறம் உள்ள பட்டையை தொடவும்.",
    bn: "যে গান দেখাবে, সেটিতে চাপুন। আওয়াজ বাড়াতে ডানদিকের পট্টি স্পর্শ করুন।",
    mr: "जो गाणं दिसेल त्यावर दाबा. आवाज वाढवण्यासाठी उजव्या बाजूच्या पट्टीला स्पर्श करा.",
    gu: "જે ગીત દેખાય તે પર દબાવો. અવાજ વધારવા જમણી બાજુની પટ્ટીને સ્પર્શ કરો.",
    en: "Tap the song you see. To raise the volume, touch the right-side bar.",
  },
  // Settings font size
  "task.settings.font_size_increase.label": {
    hi: "अक्षर बड़े करना",
    ta: "எழுத்தை பெரிதாக்குதல்",
    bn: "অক্ষর বড় করা",
    mr: "अक्षरं मोठी करणं",
    gu: "અક્ષરો મોટા કરવા",
    en: "Make the text bigger",
  },
  "task.settings.font_size_increase.step1": {
    hi: "सेटिंग्स ऐप खोलिए — दाँत वाला चिन्ह।",
    ta: "அமைப்புகள் பயன்பாட்டை திறக்கவும் — பல் சக்கர சின்னம்.",
    bn: "সেটিংস অ্যাপ খুলুন — গিয়ার চিহ্ন।",
    mr: "सेटिंग्ज अ‍ॅप उघडा — गियरचं चिन्ह.",
    gu: "સેટિંગ્સ એપ ખોલો — ગિયર ચિહ્ન.",
    en: "Open the Settings app — the gear icon.",
  },
  "task.settings.font_size_increase.step2": {
    hi: "'डिस्प्ले' या 'दृश्य' पर दबाइए, फिर 'फ़ॉन्ट साइज़' खोजिए।",
    ta: "'காட்சி' அல்லது 'Display' அழுத்துங்கள், பிறகு 'எழுத்து அளவு' தேடுங்கள்.",
    bn: "'ডিসপ্লে' বা 'প্রদর্শন' চাপুন, তারপর 'ফন্ট সাইজ' খুঁজুন।",
    mr: "'डिस्प्ले' किंवा 'दृश्य' वर दाबा, नंतर 'फॉन्ट साइज' शोधा.",
    gu: "'ડિસ્પ્લે' અથવા 'દૃશ્ય' પર દબાવો, પછી 'ફોન્ટ સાઇઝ' શોધો.",
    en: "Tap 'Display', then find 'Font size'.",
  },
  "task.settings.font_size_increase.step3": {
    hi: "नीचे दिए गए घुंडी को दाहिने तरफ खिसकाइए जब तक अक्षर पढ़ने में आसान न हो जाएँ।",
    ta: "எழுத்து வாசிக்க எளிதாகும் வரை கீழே உள்ள ஸ்லைடரை வலதுபுறம் நகர்த்தவும்.",
    bn: "নিচের স্লাইডার ডানদিকে সরান যতক্ষণ না অক্ষর পড়তে সহজ হয়।",
    mr: "खालची स्लायडर उजव्या बाजूला सरकवा जोपर्यंत अक्षरं वाचायला सोपी होत नाहीत.",
    gu: "નીચેની સ્લાઇડરને જમણી બાજુ સરકાવો જ્યાં સુધી અક્ષરો વાંચવા સરળ ન થાય.",
    en: "Slide the bar to the right until the text is easy to read.",
  },
  // Accessibility zoom
  "task.accessibility.enable_zoom.label": {
    hi: "स्क्रीन को बड़ा करना",
    ta: "திரையை பெரிதாக்குதல்",
    bn: "স্ক্রিন বড় করা",
    mr: "स्क्रीन मोठी करणं",
    gu: "સ્ક્રીન મોટી કરવી",
    en: "Enable screen zoom",
  },
  "task.accessibility.enable_zoom.step1": {
    hi: "सेटिंग्स में 'ऐक्सेसिबिलिटी' या 'सुगमता' ढूँढिए।",
    ta: "அமைப்புகளில் 'அணுகுதன்மை' அல்லது 'Accessibility' கண்டறியவும்.",
    bn: "সেটিংসে 'অ্যাক্সেসিবিলিটি' খুঁজুন।",
    mr: "सेटिंग्जमध्ये 'अ‍ॅक्सेसिबिलिटी' शोधा.",
    gu: "સેટિંગ્સમાં 'એક્સેસિબિલિટી' શોધો.",
    en: "In Settings, find 'Accessibility'.",
  },
  "task.accessibility.enable_zoom.step2": {
    hi: "'मेग्निफ़ायर' या 'ज़ूम' विकल्प पर दबाइए।",
    ta: "'மெக்னிஃபையர்' அல்லது 'Zoom' விருப்பத்தை அழுத்துங்கள்.",
    bn: "'ম্যাগনিফায়ার' বা 'জুম' বিকল্পে চাপুন।",
    mr: "'मॅग्निफायर' किंवा 'झूम' पर्यायावर दाबा.",
    gu: "'મેગ્નિફાયર' અથવા 'ઝૂમ' વિકલ્પ પર દબાવો.",
    en: "Tap 'Magnifier' or 'Zoom'.",
  },
  "task.accessibility.enable_zoom.step3": {
    hi: "स्विच को 'चालू' की तरफ खिसकाइए। अब तीन उँगली से छूकर पूरी स्क्रीन बड़ी होगी।",
    ta: "சுவிட்சை 'On' பக்கம் நகர்த்தவும். இப்போது மூன்று விரல்களால் தொட்டால் முழுத் திரையும் பெரிதாகும்.",
    bn: "সুইচটি 'চালু' দিকে সরান। এখন তিন আঙুলে স্পর্শ করলে পুরো স্ক্রিন বড় হবে।",
    mr: "स्विच 'चालू' बाजूला सरकवा. आता तीन बोटांनी स्पर्श करून संपूर्ण स्क्रीन मोठी होईल.",
    gu: "સ્વિચને 'ચાલુ' તરફ સરકાવો. હવે ત્રણ આંગળીથી સ્પર્શ કરી આખી સ્ક્રીન મોટી થશે.",
    en: "Slide the switch to 'On'. Now a three-finger tap will zoom the screen.",
  },
  // --- Family-Mirror (F42)
  "mirror.consent.title": {
    hi: "स्क्रीन साझा करें?",
    ta: "திரையை பகிர்வீர்களா?",
    bn: "স্ক্রিন শেয়ার করবেন?",
    mr: "स्क्रीन शेअर करायची?",
    gu: "સ્ક્રીન શેર કરશો?",
    en: "Share your screen?",
  },
  "mirror.consent.body": {
    hi: "आपकी स्क्रीन देखेगा। आपका कैमरा, माइक, बैंक खाता नहीं। आप कभी भी रोक सकते हैं।",
    ta: "உங்கள் திரையை பார்ப்பார். உங்கள் கேமரா, மைக், வங்கி இல்லை. நீங்கள் எப்போது வேண்டுமானாலும் நிறுத்தலாம்.",
    bn: "আপনার স্ক্রিন দেখবেন। আপনার ক্যামেরা, মাইক, ব্যাঙ্ক নয়। আপনি যেকোনো সময় থামাতে পারেন।",
    mr: "तुमची स्क्रीन पाहतील. तुमचा कॅमेरा, माइक, बँक नाही. तुम्ही कधीही थांबवू शकता.",
    gu: "તમારી સ્ક્રીન જોશે. તમારો કેમેરા, માઇક, બેંક નહીં. તમે ગમે ત્યારે રોકી શકો છો.",
    en: "Will see your screen. Not your camera, mic, or bank. You can stop anytime.",
  },
  "mirror.consent.allow": {
    hi: "हाँ, साझा करें",
    ta: "ஆம், பகிர்",
    bn: "হ্যাঁ, শেয়ার",
    mr: "हो, शेअर करा",
    gu: "હા, શેર કરો",
    en: "Yes, share",
  },
  "mirror.consent.deny": {
    hi: "नहीं, अभी नहीं",
    ta: "இல்லை, இப்போது இல்லை",
    bn: "না, এখন নয়",
    mr: "नाही, आता नाही",
    gu: "ના, હમણાં નહીં",
    en: "No, not now",
  },
  "mirror.session.ended": {
    hi: "स्क्रीन साझा करना बंद हुआ।",
    ta: "திரை பகிர்வு முடிந்தது.",
    bn: "স্ক্রিন শেয়ারিং শেষ।",
    mr: "स्क्रीन शेअरिंग संपले.",
    gu: "સ્ક્રીન શેરિંગ બંધ થયું.",
    en: "Screen sharing has ended.",
  },
  "mirror.session.live": {
    hi: "स्क्रीन साझा चल रही है",
    ta: "திரை பகிரப்படுகிறது",
    bn: "স্ক্রিন শেয়ার চলছে",
    mr: "स्क्रीन शेअर सुरू आहे",
    gu: "સ્ક્રીન શેર ચાલુ છે",
    en: "Screen sharing is live",
  },
  // --- Remote pin (F42)
  "pin.new_request": {
    hi: "ने आपके फ़ोन पर एक नया बटन भेजा है। स्वीकार करें?",
    ta: "உங்கள் தொலைபேசிக்கு புதிய பொத்தான் அனுப்பியுள்ளார். ஏற்றுக்கொள்ளவா?",
    bn: "আপনার ফোনে নতুন বোতাম পাঠিয়েছেন। গ্রহণ করবেন?",
    mr: "तुमच्या फोनवर नवीन बटण पाठवले आहे. स्वीकारायचे का?",
    gu: "તમારા ફોન પર નવું બટન મોકલ્યું છે. સ્વીકારશો?",
    en: "sent a new button to your phone. Accept?",
  },
  "pin.accept": {
    hi: "हाँ, जोड़ें",
    ta: "ஆம், சேர்",
    bn: "হ্যাঁ, যোগ করুন",
    mr: "हो, जोडा",
    gu: "હા, ઉમેરો",
    en: "Yes, add",
  },
  "pin.dismiss": {
    hi: "नहीं, अभी नहीं",
    ta: "இல்லை, இப்போது இல்லை",
    bn: "না, এখন নয়",
    mr: "नाही, आता नाही",
    gu: "ના, હમણાં નહીં",
    en: "No, not now",
  },
  // --- Onboarding (F42)
  "onboard.welcome": {
    hi: "स्वागत है। चलिए सारथी को आपके लिए तैयार करते हैं।",
    ta: "வரவேற்கிறோம். சாரதியை உங்களுக்காக தயார் செய்வோம்.",
    bn: "স্বাগতম। চলুন সারথিকে আপনার জন্য তৈরি করি।",
    mr: "स्वागत आहे. चला सारथीला तुमच्यासाठी तयार करूया.",
    gu: "સ્વાગત છે. ચાલો સારથિને તમારા માટે તૈયાર કરીએ.",
    en: "Welcome. Let us set up Sarathi for you.",
  },
  "onboard.step.language": {
    hi: "अपनी भाषा चुनिए",
    ta: "உங்கள் மொழியை தேர்வு செய்யுங்கள்",
    bn: "আপনার ভাষা বেছে নিন",
    mr: "तुमची भाषा निवडा",
    gu: "તમારી ભાષા પસંદ કરો",
    en: "Choose your language",
  },
  "onboard.step.biometric": {
    hi: "उँगली या चेहरे से लॉक करें",
    ta: "விரல் அல்லது முகத்துடன் பூட்டவும்",
    bn: "আঙুল বা মুখ দিয়ে লক করুন",
    mr: "बोट किंवा चेहऱ्याने लॉक करा",
    gu: "આંગળી અથવા ચહેરાથી લોક કરો",
    en: "Lock with fingerprint or face",
  },
  "onboard.step.family": {
    hi: "अपने परिवार के 3 लोग जोड़ें",
    ta: "உங்கள் குடும்பத்தில் 3 பேரை சேர்க்கவும்",
    bn: "আপনার পরিবারের ৩ জনকে যোগ করুন",
    mr: "तुमच्या कुटुंबातील 3 लोक जोडा",
    gu: "તમારા પરિવારના 3 લોકોને ઉમેરો",
    en: "Add 3 people from your family",
  },
  "onboard.step.emergency": {
    hi: "आपातकालीन नंबर",
    ta: "அவசர எண்கள்",
    bn: "জরুরি নম্বর",
    mr: "आपत्कालीन क्रमांक",
    gu: "ઈમરજન્સી નંબર",
    en: "Emergency numbers",
  },
  "onboard.step.medical": {
    hi: "स्वास्थ्य की जानकारी (अगर देना चाहें)",
    ta: "சுகாதார தகவல் (கொடுக்க விரும்பினால்)",
    bn: "স্বাস্থ্য তথ্য (যদি দিতে চান)",
    mr: "आरोग्य माहिती (द्यायची असल्यास)",
    gu: "આરોગ્ય માહિતી (આપવા માગો તો)",
    en: "Health information (if you wish to share)",
  },
  "onboard.complete": {
    hi: "हो गया। अब आप सारथी का उपयोग कर सकते हैं।",
    ta: "முடிந்தது. நீங்கள் இப்போது சாரதியை பயன்படுத்தலாம்.",
    bn: "হয়ে গেল। এখন আপনি সারথি ব্যবহার করতে পারেন।",
    mr: "झाले. आता तुम्ही सारथी वापरू शकता.",
    gu: "થઈ ગયું. હવે તમે સારથિ વાપરી શકો છો.",
    en: "Done. You can start using Sarathi now.",
  },
  "onboard.next": {
    hi: "आगे बढ़ें",
    ta: "தொடரவும்",
    bn: "এগিয়ে যান",
    mr: "पुढे जा",
    gu: "આગળ વધો",
    en: "Continue",
  },
  "onboard.skip": {
    hi: "इसे छोड़ें",
    ta: "இதை தவிர்",
    bn: "এটি এড়িয়ে যান",
    mr: "हे सोडा",
    gu: "આ છોડો",
    en: "Skip this",
  },
  // --- Home grid (F42)
  "home.tile.phone": {
    hi: "फ़ोन",
    ta: "தொலைபேசி",
    bn: "ফোন",
    mr: "फोन",
    gu: "ફોન",
    en: "Phone",
  },
  "home.tile.whatsapp": {
    hi: "व्हाट्सऐप",
    ta: "வாட்ஸ்அப்",
    bn: "হোয়াটসঅ্যাপ",
    mr: "व्हॉट्सअ‍ॅप",
    gu: "વ્હોટ્સએપ",
    en: "WhatsApp",
  },
  "home.tile.camera": {
    hi: "कैमरा",
    ta: "கேமரா",
    bn: "ক্যামেরা",
    mr: "कॅमेरा",
    gu: "કેમેરા",
    en: "Camera",
  },
  "home.tile.photos": {
    hi: "तस्वीरें",
    ta: "புகைப்படங்கள்",
    bn: "ছবি",
    mr: "फोटो",
    gu: "ફોટા",
    en: "Photos",
  },
  "home.tile.sos": {
    hi: "आपातकाल",
    ta: "அவசரம்",
    bn: "জরুরি",
    mr: "तातडी",
    gu: "તાત્કાલિક",
    en: "Emergency",
  },
  // --- Settings (F42)
  "settings.font.bada": {
    hi: "बड़ा",
    ta: "பெரிது",
    bn: "বড়",
    mr: "मोठे",
    gu: "મોટું",
    en: "Big",
  },
  "settings.font.aur_bada": {
    hi: "और बड़ा",
    ta: "இன்னும் பெரிது",
    bn: "আরও বড়",
    mr: "अजून मोठे",
    gu: "વધુ મોટું",
    en: "Bigger",
  },
  "settings.font.sabse_bada": {
    hi: "सबसे बड़ा",
    ta: "மிகப் பெரிது",
    bn: "সবচেয়ে বড়",
    mr: "सर्वात मोठे",
    gu: "સૌથી મોટું",
    en: "Biggest",
  },
  "settings.speech.dhire": {
    hi: "धीरे",
    ta: "மெதுவாக",
    bn: "ধীরে",
    mr: "हळू",
    gu: "ધીરે",
    en: "Slow",
  },
  "settings.speech.normal": {
    hi: "साधारण",
    ta: "சாதாரண",
    bn: "সাধারণ",
    mr: "सामान्य",
    gu: "સામાન્ય",
    en: "Normal",
  },
  "settings.speech.tez": {
    hi: "तेज़",
    ta: "வேகமாக",
    bn: "দ্রুত",
    mr: "जलद",
    gu: "ઝડપી",
    en: "Fast",
  },
};

export function knownLangsForKey(key: string): readonly LangCode[] {
  const entry = i18n[key];
  if (!entry) return [];
  return (Object.keys(entry) as LangCode[]);
}
